<script setup lang="ts">
import type { InfoNoteTone } from '~/features/my-wallet/types/info-note'
import type { PerformanceView } from '~/features/my-wallet/types/receiving-model'
import { I18nT, useI18n } from 'vue-i18n'
import IconStar from '~icons/material-symbols/star'
import PerformanceBar from '~/features/my-wallet/components/charts/PerformanceBar.vue'
import InfoNote from '~/features/my-wallet/components/InfoNote.vue'
import { PERFORMANCE_WINDOW_DAYS, REASON_KEY_PREFIXES } from '~/features/my-wallet/constants/my-wallet'
import { matchContextNote } from '~/features/my-wallet/utils/context-note'
import { resolveReasonKey } from '~/features/my-wallet/utils/my-wallet'
import CompletionBreakdown from './CompletionBreakdown.vue'

interface Props {
  performance: PerformanceView
}

const props = defineProps<Props>()

const { t, te } = useI18n()

function joinModelLabels(modelLabels: string[]) {
  if (modelLabels.length <= 1)
    return modelLabels[0] ?? ''
  return t('my_wallet.list_and', { items: modelLabels.slice(0, -1).join(t('my_wallet.list_separator')), last: modelLabels[modelLabels.length - 1] })
}

// The "*" points to "keep delinquency under control", a note the commercial exception does not show.
const enabledModelsText = computed(() => joinModelLabels(props.performance.enabledModels.map(model => model === 'A' && !props.performance.commercialException
  ? t('my_wallet.receiving_model.performance.requires_delinquency_mark', { model })
  : model)))

const showBarLegend = computed(() => props.performance.commercialException && !props.performance.hasNoCompletions)

const enablesKeypath = computed(() => props.performance.enabledModels.length > 1
  ? 'my_wallet.receiving_model.performance.enables_many'
  : 'my_wallet.receiving_model.performance.enables_one')

type ContextNoteView
  = | { layout: 'column', isCommercial: boolean, label: string, value: string, caption: string }
    | { layout: 'notice', tone: InfoNoteTone, title: string, reason: string }

const contextNoteView = computed(() => {
  const contextNote = props.performance.contextNote
  if (!contextNote)
    return null

  return matchContextNote<ContextNoteView | null>(contextNote, {
    commercial: () => props.performance.commercialModels.length === 0
      ? null
      : {
          layout: 'column',
          isCommercial: true,
          label: t('my_wallet.receiving_model.performance.commercial_label'),
          value: t(
            'my_wallet.receiving_model.performance.commercial_value',
            { models: joinModelLabels(props.performance.commercialModels) },
            props.performance.commercialModels.length,
          ),
          caption: t('my_wallet.receiving_model.performance.commercial_caption'),
        },
    next: nextNote => ({
      layout: 'column',
      isCommercial: false,
      label: t('my_wallet.receiving_model.performance.challenge_label'),
      value: t('my_wallet.receiving_model.performance.challenge_value', { count: nextNote.count }),
      caption: t('my_wallet.receiving_model.performance.challenge_caption', { model: nextNote.model }),
    }),
    keep: () => ({
      layout: 'column',
      isCommercial: false,
      label: t('my_wallet.receiving_model.performance.challenge_label'),
      value: t('my_wallet.receiving_model.performance.keep_value'),
      caption: t('my_wallet.receiving_model.performance.keep_caption'),
    }),
    blocked: () => ({
      layout: 'notice',
      tone: 'danger',
      title: t('my_wallet.receiving_model.performance.blocked_title'),
      reason: t('my_wallet.receiving_model.performance.blocked_reason'),
    }),
    disabled: disabledNote => ({
      layout: 'notice',
      tone: 'warning',
      title: t('my_wallet.receiving_model.performance.disabled_title', { model: disabledNote.model }),
      reason: t('my_wallet.receiving_model.performance.disabled_reason', {
        reason: t(resolveReasonKey(REASON_KEY_PREFIXES.MODEL_DISABLED, disabledNote.reason, te)),
      }),
    }),
    no_completions: () => null,
  })
})
</script>

<template>
  <div class="performance-summary">
    <div v-if="performance.commercialException" class="performance-summary-banner">
      <IconStar class="performance-summary-banner-icon" aria-hidden="true" />
      <p>
        <strong>{{ t('my_wallet.receiving_model.performance.not_evaluated_banner.title') }}</strong>
        <br>
        {{ t('my_wallet.receiving_model.performance.not_evaluated_banner.description') }}
      </p>
    </div>

    <div class="performance-summary-card">
      <div class="performance-summary-stats">
        <div class="performance-summary-stat">
          <p class="performance-summary-label">
            {{ t('my_wallet.receiving_model.performance.total_label') }}
          </p>
          <div class="performance-summary-values">
            <span class="performance-summary-value">
              {{ t('my_wallet.receiving_model.performance.total_value', { count: performance.totalCompleted }, performance.totalCompleted) }}
            </span>
            <I18nT keypath="my_wallet.receiving_model.performance.total_caption" tag="span" class="performance-summary-caption">
              <template #window>
                <strong>{{ t('my_wallet.receiving_model.performance.window', { days: PERFORMANCE_WINDOW_DAYS }) }}</strong>
              </template>
            </I18nT>
          </div>
        </div>

        <div class="performance-summary-stat">
          <p class="performance-summary-label">
            <span v-if="showBarLegend" class="performance-summary-dot -model-b" aria-hidden="true" />
            {{ t('my_wallet.receiving_model.performance.current_label') }}
          </p>
          <div v-if="performance.hasNoCompletions" class="performance-summary-values">
            <span class="performance-summary-value">
              {{ t('my_wallet.no_completions.value') }}
            </span>
            <span class="performance-summary-caption">
              {{ t('my_wallet.no_completions.caption', { days: PERFORMANCE_WINDOW_DAYS }) }}
            </span>
          </div>
          <div v-else class="performance-summary-values">
            <span class="performance-summary-value">
              {{ t('my_wallet.receiving_model.performance.current_value', { percentage: performance.percentage }) }}
            </span>
            <span v-if="performance.enabledModels.length === 0" class="performance-summary-caption">
              {{ t('my_wallet.receiving_model.performance.enables_none') }}
            </span>
            <I18nT v-else :keypath="enablesKeypath" tag="span" class="performance-summary-caption">
              <template #models>
                <strong>{{ t('my_wallet.receiving_model.performance.enabled_models', { models: enabledModelsText }) }}</strong>
              </template>
            </I18nT>
          </div>
        </div>

        <div v-if="contextNoteView?.layout === 'column'" class="performance-summary-stat">
          <p class="performance-summary-label">
            <span v-if="contextNoteView.isCommercial && showBarLegend" class="performance-summary-dot -waived" aria-hidden="true" />
            {{ contextNoteView.label }}
          </p>
          <div class="performance-summary-values">
            <span class="performance-summary-value" :class="{ '-commercial': contextNoteView.isCommercial }">
              {{ contextNoteView.value }}
            </span>
            <span class="performance-summary-caption">
              {{ contextNoteView.caption }}
            </span>
          </div>
        </div>

        <InfoNote
          v-else-if="contextNoteView?.layout === 'notice'"
          :tone="contextNoteView.tone"
          class="performance-summary-notice"
        >
          <template #title>
            <p class="performance-summary-notice-title">
              {{ contextNoteView.title }}
            </p>
          </template>
          <p class="performance-summary-notice-reason">
            {{ contextNoteView.reason }}
          </p>
        </InfoNote>
      </div>

      <InfoNote v-if="performance.noCompletionsNotice" tone="info">
        <template #title>
          <p><strong>{{ t('my_wallet.no_completions.title', { days: PERFORMANCE_WINDOW_DAYS }) }}</strong></p>
        </template>
        <p>{{ t('my_wallet.no_completions.description', { model: performance.noCompletionsNotice.model }) }}</p>
      </InfoNote>
      <PerformanceBar
        v-else-if="!performance.hasNoCompletions"
        variant="full"
        :percentage="performance.percentage"
        :thresholds="performance.thresholds"
        :muted="performance.muted"
        :commercial-exception="performance.commercialException"
        :label-states="performance.labelStates"
      />
    </div>

    <CompletionBreakdown v-if="!performance.hasNoCompletions" :performance="performance" />
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/model-tones' as tones;
@use '../../styles/typography';

.performance-summary {
  @apply flex flex-col gap-6 w-full;

  .performance-summary-banner {
    @apply flex items-center gap-2.5 px-4 py-3.5 rounded-md;
    @apply bg-neutral-high-light border border-neutral-high-medium;
    @apply fonts-body-medium-regular text-neutral-low-dark;
  }

  .performance-summary-banner-icon {
    @apply w-4 h-4 flex-shrink-0 text-neutral-low-medium;
  }

  .performance-summary-card {
    @apply flex flex-col gap-6 w-full p-6 rounded-lg;
    @apply bg-neutral-high-pure border border-neutral-high-medium;
  }

  .performance-summary-stats {
    @apply flex flex-col gap-6;

    @screen sm:system {
      @apply flex-row flex-wrap items-start;
      column-gap: 5rem;
    }
  }

  .performance-summary-stat {
    @apply flex flex-col;
    gap: 10px;
  }

  .performance-summary-label {
    @apply flex items-center uppercase font-bold text-brand-primary-medium;
    gap: 6px;
    font-size: 0.625rem;
    line-height: 1.5;
  }

  .performance-summary-dot {
    @apply w-2 h-2 rounded-full flex-shrink-0;
    @include tones.tone-modifiers(bg, ('model-b', 'waived'));
  }

  .performance-summary-values {
    @apply flex flex-col;
    gap: 2px;
  }

  .performance-summary-value {
    @apply text-neutral-low-dark;
    @include typography.title-20;

    @screen md:system {
      @apply whitespace-nowrap;
    }

    &.-commercial {
      @apply text-brand-secondary-dark;
    }
  }

  .performance-summary-caption {
    @apply fonts-body-medium-regular text-brand-primary-medium;
  }

  .performance-summary-notice {
    @apply self-start;
  }

  .performance-summary-notice-title {
    @apply fonts-body-medium-bold;
    line-height: 1.5;
  }

  .performance-summary-notice-reason {
    @apply fonts-body-small-bold;
    line-height: 1.5;
  }
}
</style>
